import type { AppErrorKey } from "@/types/generatedErrorMessageKey";

import { GLOBAL_ERROR_HANDLERS } from "@/apollo/handlers/errorHandlers";
import i18n from "@/i18n";
import { setDeep } from "@/utils/formik";

export type TFieldErrors = {
  message: string;
  messageKey: string;
  invalidValue: unknown;
}[];

type TValidationErrors = Record<string, TFieldErrors>;

export type TFieldMapping = Record<string, string>;

export type RecursiveErrorNode =
  | string
  | Array<RecursiveErrorNode>
  | { [key: string]: RecursiveErrorNode }
  | undefined
  | null;

export interface IServerError {
  errors: {
    message: string;
    messageKey: string;
    code: number;
    fields?: TValidationErrors;
  }[];
}

const FIELD_ERROR_DELIMITER = "\u241E";

function serializeFieldErrors(errors: TFieldErrors): string {
  if (!Array.isArray(errors)) return "";

  return errors
    .filter((e) => e.messageKey)
    .map((e) => {
      return i18n.t(`errors:${e.messageKey}`, {
        defaultValue: e.messageKey,
      });
    })

    .join(FIELD_ERROR_DELIMITER);
}

export function normalizeServerValidationErrors(
  error: unknown,
  fieldMapping: TFieldMapping = {}
): Record<string, string> {
  const fields = (error as IServerError)?.errors?.[0]?.fields ?? {};
  const result = {};

  Object.entries(fields).forEach(([serverKey, value]) => {
    const clientKey = fieldMapping[serverKey] || serverKey;
    const errorMessage = serializeFieldErrors(value);
    setDeep(result, clientKey, errorMessage);
  });

  return result;
}

export function parseFieldErrors(errors: string): string[] {
  return errors.split(FIELD_ERROR_DELIMITER);
}

export function serverGeneralErrorKey(error: unknown): AppErrorKey | undefined {
  const serverError = error as IServerError;
  if (!serverError?.errors || !Array.isArray(serverError.errors)) {
    return undefined;
  }

  const globalErrorValues: string[] = Object.values(GLOBAL_ERROR_HANDLERS);
  const relevantErrors = serverError.errors.filter((err) => {
    const key = err.messageKey as string | undefined;
    return key && !globalErrorValues.includes(key);
  });

  if (relevantErrors.length === 0) {
    return undefined;
  }

  return relevantErrors[0].messageKey as AppErrorKey;
}

export function serverGeneralErrorTranslatedValue(
  error: unknown
): string | undefined {
  const key = serverGeneralErrorKey(error);
  if (!key) return undefined;
  return i18n.t(`errors:${key}`, { defaultValue: key });
}
